import { Box, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useContext, useEffect, useState } from "react";
import AddressCard from "../../../shared/components/Addresses/AddressCard";
import SettingsSection from "../../../shared/components/SettingsSection";
import {
  ActionButton,
  TableActions,
  TableCell,
  TableRow,
} from "../../../shared/components/table/StyledTable";
import { PartitionKeyEnum } from "../../../shared/enums";
import { isSure } from "../../../shared/helpers";
import PartnerProvider, {
  PartnerContext,
} from "../../../shared/providers/PartnerProvider";
import { DELETE_PARTNER, GET_ALL_PARTNERS } from "../../../shared/reducer";
import { formatCurrency } from "../../../shared/utils";
import PartnersDialog from "./PartnersDialog";

const Partners = () => (
  <PartnerProvider>
    <PartnersConsumer />
  </PartnerProvider>
);

/**
 * Table overview of partners with CRUD abilities
 */
const PartnersConsumer = () => {
  // Load state and reducer dispatch
  const { state, dispatch } = useContext(PartnerContext);

  // Load partners on render
  useEffect(() => {
    dispatch({ type: GET_ALL_PARTNERS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dialog/current id being edited state and handlers
  const [dialogOpen, setDialogOpen] = useState(false);
  const [id, setId] = useState<string>("");
  const handleOpenDialog = (nextId: string) => {
    setId(nextId);
    setDialogOpen(true);
  };
  const handleCloseDialog = () => {
    setId("");
    setDialogOpen(false);
  };

  // Partner deletion fn
  const handleDelete = async (partnerId: string) => {
    if (!(await isSure())) return;
    dispatch({
      type: DELETE_PARTNER,
      id: partnerId,
    });
  };

  return (
    <>
      <SettingsSection
        title="Partner"
        description="Vermittelnde Partner und deren Leistungen"
        columns={["ID", "Ansprechpartner", "Icon", "Leistungen"]}
        onAdd={() => handleOpenDialog("")}
        addLabel="Partner"
        isEmpty={!state?.length}
        emptyMessage="Zurzeit sind keine Partner im System vorhanden."
      >
        {state
          ?.slice()
          ?.sort((a: any, b: any) => a.id.localeCompare(b.id))
          ?.map(({ contact, icon, id: partnerId, prices, services }: any) => (
            <TableRow key={partnerId}>
              <TableCell sx={{ fontWeight: 600 }}>{partnerId}</TableCell>
              <TableCell>
                <AddressCard address={contact} />
              </TableCell>
              <TableCell>
                {icon && (
                  <Box
                    component="img"
                    src={icon}
                    alt={partnerId}
                    sx={{ height: 36, maxWidth: 120, objectFit: "contain" }}
                  />
                )}
              </TableCell>
              <TableCell>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 0, listStyle: "none", display: "grid", gap: 0.25 }}
                >
                  {services?.map((service: string) => (
                    <Typography
                      component="li"
                      variant="body2"
                      key={service}
                      sx={{ color: "text.secondary" }}
                    >
                      {
                        //@ts-ignore - string index without enum
                        PartitionKeyEnum[service]
                      }
                      : {formatCurrency(prices[service])}
                    </Typography>
                  ))}
                </Box>
              </TableCell>
              <TableCell align="right">
                <TableActions>
                  <ActionButton
                    icon={<EditIcon />}
                    label="Partner bearbeiten"
                    onClick={() => handleOpenDialog(partnerId)}
                  />
                  <ActionButton
                    icon={<DeleteIcon />}
                    label="Partner löschen"
                    onClick={() => handleDelete(partnerId)}
                  />
                </TableActions>
              </TableCell>
            </TableRow>
          ))}
      </SettingsSection>

      {dialogOpen && (
        <PartnersDialog id={id} handleCloseDialog={handleCloseDialog} />
      )}
    </>
  );
};

export default Partners;
